
import { translate } from '../../i18n/translate';
import { Dispatch } from "react"
import apis from "../../utils/apis"
import { AnyAction } from "redux"
import { AISettings } from "../reducer/DataReducer"
import { showFeedbackModal } from "./UserFeedbackActions"
import { GET_AI_SETTINGS_SUCCESS, GET_PREFERENCES_SUCCESS, GET_USER_MANUAL_FAILURE, GET_USER_MANUAL_REQUEST, GET_USER_MANUAL_SUCCESS, SET_AI_SETTINGS, SET_CHAT_LIST, SET_CITY, SET_COUNTRY, SET_CURRENCY, SET_MESSAGE_LIST, SET_PREFERENCES, SET_STATE, SET_USER_TYPES } from "../types"
import { decryptMessage, decryptWithRecipientPubKey, getPrivateKey } from "../../components/MessageCenter/E2EEHelpers"
import { handleApiError } from "./utils/utils"
import { withJurisdictionKeys, withLanguage } from "../../utils/aiPreferences"
import { HelpData } from "../../components/Help/Manual/types"
import { fetchMenuStructure } from "./MenuActions"



export interface UserInfo {
    City: string;
    Country: string;
    DateOfBirth: string;
    Email: string;
    Employer: string;
    FirstName: string;
    KYCStatus: string;
    LastName: string;
    LawfirmID?: string;
    Nationality: string;
    Occupation: string;
    PhoneNumber: string;
    ProfilePicture: File | string;
    State: string;
    StreetAddress: string;
    UserID: string;
    UserType: string;
    ZipCode: string;
}

interface GetUserManualRequestAction {
    type: typeof GET_USER_MANUAL_REQUEST;
}

interface GetUserManualSuccessAction {
    type: typeof GET_USER_MANUAL_SUCCESS;
    payload: HelpData;
}

interface GetUserManualFailureAction {
    type: typeof GET_USER_MANUAL_FAILURE;
    payload: string;
}

export type HelpActionTypes =
    | GetUserManualRequestAction
    | GetUserManualSuccessAction
    | GetUserManualFailureAction;

export const setCountry = (countries: string) => {
    return {
        type: SET_COUNTRY,
        countries
    }

}

export const setCurrency = (currencies: any) => {
    return {
        type: SET_CURRENCY,
        currencies
    };
};
export const setState = (states: string) => {
    return {
        type: SET_STATE,
        states
    }
}

export const setCity = (cities: string) => {
    return {
        type: SET_CITY,
        cities
    }
}

export const setPreferences = (preferences: any) => {
    return {
        type: SET_PREFERENCES,
        preferences,
    };
};

export const getPreferencesSuccess = (preferences: any) => {
    return {
        type: GET_PREFERENCES_SUCCESS,
        preferences,
    };
};

export const setAISettings = (aiSettings: any) => ({
    type: SET_AI_SETTINGS,
    aiSettings,
});

export const getAISettingsSuccess = (aiSettings: any) => ({
    type: GET_AI_SETTINGS_SUCCESS,
    aiSettings,
});


export const setChatList = (chat_list: any[]) => ({
    type: SET_CHAT_LIST,
    chat_list
})

export const setUserTypes = (userTypes: any[]) => ({
    type: SET_USER_TYPES,
    userTypes
})

export const setMessageList = (message_list: any[]) => ({
    type: SET_MESSAGE_LIST,
    message_list
})

/** Where the app-boot preferences hydration has got to. */
export type PreferencesStatus = 'idle' | 'loading' | 'ready' | 'error';

export const PREFERENCES_HYDRATING = 'PREFERENCES_HYDRATING';
export const PREFERENCES_HYDRATED = 'PREFERENCES_HYDRATED';
export const PREFERENCES_HYDRATION_FAILED = 'PREFERENCES_HYDRATION_FAILED';

interface UserState {
    userInfo: any;
    tutorialCompleted: boolean;
    preferencesStatus: PreferencesStatus;
}

const initialState: UserState = {
    userInfo: {},
    tutorialCompleted: false,
    preferencesStatus: 'idle',
};

/**
 * Merge preferences WITHOUT letting a partial payload delete a whole section.
 *
 * `preferences` is one object holding two independent sections
 * (`general_settings`, `ai_settings`), but it arrives from several endpoints
 * that each know about different parts of it. A top-level spread therefore
 * REPLACED the object: `/user/userinfo` returning a preferences blob without
 * `ai_settings` silently wiped the user's saved jurisdiction and language out
 * of the store, and every service that reads its default from the store then
 * showed "Not set" for a preference the user had definitely saved.
 *
 * The bootstrap races those endpoints (`PrivateLayout` fires them in a
 * `Promise.all`), so which one landed last decided whether your preferences
 * existed. Merging section by section makes the race unobservable.
 */
const mergeUserPreferences = (existing: any, incoming: any) => {
    if (!incoming) return existing;
    if (!existing) return incoming;
    return {
        ...existing,
        ...incoming,
        general_settings: {
            ...(existing.general_settings || {}),
            ...(incoming.general_settings || {}),
        },
        ai_settings: {
            ...(existing.ai_settings || {}),
            ...(incoming.ai_settings || {}),
        },
    };
};

export const userReducer = (state = initialState, action: AnyAction): UserState => {
    switch (action.type) {
        case 'STORE_USER_INFO':
            // Merge incoming user data with existing state to preserve critical fields
            // This prevents partial API responses (e.g., from menu endpoint) from overwriting
            // complete user profile data (e.g., from auth/profile endpoint)
            const mergedUserInfo = {
                ...state.userInfo,
                ...action.payload,
            };

            // Preferences merge per section - see mergeUserPreferences. A
            // payload that carries no preferences at all leaves them alone.
            const incomingPreferences = action.payload?.preferences;
            if (incomingPreferences || state.userInfo?.preferences) {
                mergedUserInfo.preferences = mergeUserPreferences(
                    state.userInfo?.preferences,
                    incomingPreferences,
                );
            }

            // Preserve critical identity fields if they exist in current state
            // These should only be updated from the auth/profile endpoint, not from menu/UI endpoints
            if (state.userInfo?.is_org_admin !== undefined && action.payload?.is_org_admin === undefined) {
                mergedUserInfo.is_org_admin = state.userInfo.is_org_admin;
            }
            if (state.userInfo?.UserID !== undefined && action.payload?.UserID === undefined) {
                mergedUserInfo.UserID = state.userInfo.UserID;
            }
            if (state.userInfo?.UserUID !== undefined && action.payload?.UserUID === undefined) {
                mergedUserInfo.UserUID = state.userInfo.UserUID;
            }
            if (state.userInfo?.Email !== undefined && action.payload?.Email === undefined) {
                mergedUserInfo.Email = state.userInfo.Email;
            }

            return {
                ...state,
                userInfo: mergedUserInfo,
                tutorialCompleted: action.payload?.TutorialCompleted ?? state.tutorialCompleted
            };

        case 'UPDATE_USER_PREFERENCES':
            // Only update preferences without touching other user data
            // This ensures preferences updates don't clear data set by other components (like Sidebar)
            return {
                ...state,
                userInfo: {
                    ...state.userInfo,
                    preferences: mergeUserPreferences(
                        state.userInfo?.preferences,
                        action.payload.preferences,
                    ),
                },
            };

        case PREFERENCES_HYDRATING:
            return { ...state, preferencesStatus: 'loading' };

        case PREFERENCES_HYDRATED:
            return { ...state, preferencesStatus: 'ready' };

        case PREFERENCES_HYDRATION_FAILED:
            // Deliberately terminal: a failed hydration must not leave every
            // selector spinning. The user simply has no preselected default,
            // which is a valid state.
            return { ...state, preferencesStatus: 'error' };

        case 'SET_TUTORIAL_COMPLETED':
            return {
                ...state,
                tutorialCompleted: action.payload,
            };

        default:
            return state;
    }
};


export const countryList = (name?: string | null) => {
    return async (dispatch: any) => {
        try {
            let response = await apis({
                method: "GET",
                url: "territory/getAllCountries",
                data: {
                    country_name: name ? name : "",
                }
            })
            if (response && response.data) {
                dispatch(setCountry(response.data))
            }
        } catch (err) {
            dispatch(showFeedbackModal({
                modalType: 'error',
                showModal: true,
                message: (`${err}`),
                duration: 3000
            }))
        }
    }
}

export const currencyList = () => {
    return async (dispatch: any) => {
        try {
            let response = await apis({
                method: "GET",
                url: "/getCurrencyList"
            });
            if (response && response.data) {
                dispatch(setCurrency(response.data));
            }
        } catch (err) {
            dispatch(showFeedbackModal({
                modalType: 'error',
                showModal: true,
                message: (`${err}`),
                duration: 3000
            }))
        }
    };
};


export const userInformation = () => {
    return async (dispatch: Dispatch<any>) => {
        try {
            let response = await apis({
                method: "GET",
                url: "user/userinfo",
            });
            if (response && response.status === 200) {
                let userData = response.data;
                // Store only necessary non-sensitive metadata (IDs and type, not user details)
                try {
                  sessionStorage.setItem("userInfo", JSON.stringify({
                    userId: userData.UserID,
                    userType: userData.UserType,
                    kycStatus: userData.KycStatus
                  }));
                } catch (e) {
                  console.debug('[userInformation] Failed to store user metadata in sessionStorage');
                }
                dispatch({ type: 'STORE_USER_INFO', payload: userData });

                // Fetch menu structure once after user profile is loaded
                // This ensures menu is cached in Redux and persisted for zero API calls on navigation
                dispatch(fetchMenuStructure() as any);

                return userData;
            } else {
                handleApiError(response, dispatch)
            }
        } catch (err) {
            handleApiError(err, dispatch)
        }
    }
};

export const fetchUserProfile = () => {
    return async (dispatch: Dispatch<any>) => {
        try {
            const response = await apis({
                method: "GET",
                url: "/user-profile/me",
            });
            if (response && response.status === 200 && response.data?.success) {
                const userInfoData = response.data.user_info;
                if (userInfoData) {
                    // Update Redux state with user info to reflect in Sidebar, Header, etc.
                    dispatch({ type: 'STORE_USER_INFO', payload: userInfoData });
                }
                return response.data;
            } else {
                handleApiError(response, dispatch)
            }
        } catch (err) {
            handleApiError(err, dispatch)
        }
    }
};

export const EditUser = (formdata: FormData, onSuccess: () => void) => {

    return async (dispatch: Dispatch<any>) => {
        try {
            let response = await apis({
                method: 'POST',
                url: "/user/account/update_details",
                data: formdata,
                headers: {
                    'Content-Type': 'application/json'
                }
            })

            if (response && response.status === 200) {
                onSuccess()
                return response
            } else {
                handleApiError(response, dispatch)
            }
        } catch (err) {
            handleApiError(err, dispatch)
        }
    }
}


export const getPreferences = () => {
    return async (dispatch: Dispatch<any>) => {
        try {
            const response = await apis({
                method: 'GET',
                url: '/user/get_preferences',
            });
            if (response.status === 200) {
                dispatch(getPreferencesSuccess(response.data.PreferenceSettings));
            } else {
                handleApiError(response, dispatch)
            }
        } catch (error) {
            handleApiError(error, dispatch)
        }
    };
};

export const updatePreferences = (preferences: any) => {
    return async (dispatch: Dispatch<any>) => {
        try {
            const response = await apis({
                method: 'POST',
                url: '/user/edit_preferences',
                data: preferences,
            });
            if (response.status === 200) {
                dispatch(setPreferences(response.data.PreferenceSettings));
               
            } else {
                handleApiError(response, dispatch)
            }
        } catch (error) {
            handleApiError(error, dispatch)
        }
    };
};

let aiSettingsCacheTime: number | null = null;
const AI_SETTINGS_CACHE_DURATION = 5 * 60 * 1000; 

export const initializeAISettingsFromCache = () => (dispatch: Dispatch<any>) => {
    try {
        const cached = localStorage.getItem('aiSettings');
        if (cached) {
            const settings = JSON.parse(cached);
            dispatch(setAISettings(settings));
            aiSettingsCacheTime = Date.now();
            return settings;
        }
    } catch (error) {
        console.error('Error loading cached AI settings:', error);
    }
};

export const getAISettings = () => async (dispatch: Dispatch<any>, getState?: any) => {
    try {
        const now = Date.now();
        if (aiSettingsCacheTime && (now - aiSettingsCacheTime) < AI_SETTINGS_CACHE_DURATION) {
            const cachedSettings = getState?.().lists?.aiSettings;
            if (cachedSettings) {
                return cachedSettings;
            }
        }

        const response = await apis({
            method: 'GET',
            url: '/onboarding/get-preferences',
        });
        if (response.status === 200) {
            const settings: AISettings = response.data?.ai_settings || response.data;
            localStorage.setItem('aiSettings', JSON.stringify(settings));
            dispatch(setAISettings(settings));
            aiSettingsCacheTime = now;
            return settings;
        } else {
            handleApiError(response, dispatch)
        }
    } catch (error) {
        handleApiError(error, dispatch)
    }
};


export const updateAISettings = (aiSettings: AISettings) => {
    return async (dispatch: Dispatch<any>) => {
        try {
            // Dual-write: the legacy `jurisdiction` and the authoritative
            // `CountryOfJurisdiction` always go out together, so this writer
            // can never drift from the /onboarding/set-preferences one.
            const withJurisdiction = ('jurisdiction' in aiSettings || 'CountryOfJurisdiction' in aiSettings)
                ? withJurisdictionKeys(aiSettings)
                : aiSettings;
            // The output language is normalized the same way - written when
            // this update carries one, left alone when it does not.
            const payload = 'language' in withJurisdiction
                ? withLanguage(withJurisdiction)
                : withJurisdiction;
            const response = await apis({
                method: 'POST',
                url: '/ai/edit_ai_settings',
                data: payload,
            });
            if (response.status === 200) {
                localStorage.setItem('aiSettings', JSON.stringify(payload));
                dispatch(setAISettings(payload));

                aiSettingsCacheTime = Date.now();

                dispatch(showFeedbackModal({
                    showModal: true,
                    message: translate('AI settings updated'),
                    modalType: 'success',
                    duration: 3000
                }));
                return payload;
            } else {
                handleApiError(response, dispatch)
            }
        } catch (error) {
            handleApiError(error, dispatch)
        }
    };
};

export const fetchCHatList = () => {
    return async (dispatch: any) => {
        try {
            const resp: any = await apis({
                method: "GET",
                url: "chat/get_chat_list",
            })

            if (resp && resp.status === 200) {
                if (resp.data.chats_list) {
                }
                dispatch(setChatList(resp.data.chats_list))
            } else {
                handleApiError(resp, dispatch)
            }
        } catch (e) {
            handleApiError(e, dispatch)
        }
    }


}

export const fetchMessageList = (
    chat_id: string | number,
    publicKey: CryptoKey | null 
) => {
    return async (dispatch: any, getState: any) => {
        try {
            const resp: any = await apis({
                method: "POST",
                url: "chat/get_message_list",
                data: { ChatID: chat_id }
            });

            if (resp && resp.status === 200) {
                const rawMessages = resp.data.message_list;

                const { userInfo } = getState().user;
                const userUID = userInfo.UserUID;
                const myPrivateKey = await getPrivateKey(userUID);

                const decryptedMessages = [];

                for (const msg of rawMessages) {
                    let decryptedContent = "";

                    try {
                        const parsed = JSON.parse(msg.Content);
                        if (parsed.iv && parsed.ct && parsed.ephemeralPubKey) {
                            const isMyMessage = msg.sender_id || (msg.SenderID) === userUID;

                            if (isMyMessage) {
                                const ephemeralPrivKeyBase64 = parsed.ephemeralPrivKey;

                                if (publicKey && ephemeralPrivKeyBase64) {
                                    decryptedContent = await decryptWithRecipientPubKey(
                                        publicKey,                 
                                        ephemeralPrivKeyBase64,     
                                        parsed.iv,
                                        parsed.ct
                                    );
                                } else {
                                    decryptedContent = '🔒 (Missing key for own message)';
                                }

                            } else {
                                if (myPrivateKey) {
                                    decryptedContent = await decryptMessage(
                                        parsed.ephemeralPubKey,
                                        myPrivateKey,
                                        parsed.iv,
                                        parsed.ct
                                    );
                                } else {
                                    decryptedContent = '🔒 (Missing my private key)';
                                }
                            }
                        }
                    } catch (err) {
                        console.warn("Message parse or decryption failed", err);
                    }

                    decryptedMessages.push({
                        ...msg,
                        content: decryptedContent
                    });
                }

                return decryptedMessages;
            } else {
                handleApiError(resp, dispatch);
                return []
            }
        } catch (err) {
            handleApiError(err, dispatch);
            return []
        }
    };
};


export const deleteMesageChat = (chat_id: string | number) => {
    return async (dispatch: any) => {
        try {
            const resp: any = await apis({
                method: "POST",
                url: "chat/delete_chat",
                data: {
                    ChatID: chat_id
                }
            })

            if (resp && resp.status === 200) {
                dispatch(showFeedbackModal({
                    showModal: true,
                    message: resp.data.message,
                    modalType: 'success',
                    duration: 3000,
                }))
                return resp.status
            } else {
                handleApiError(resp, dispatch)
            }
        } catch (e) {
            handleApiError(e, dispatch)
        }
    }

}

export const getUserManual = (userType: string, language: string) => async (dispatch: Dispatch<any>) => {
    dispatch({ type: GET_USER_MANUAL_REQUEST });
    try {
        const response = await apis({
            method: 'POST',
            url: '/help/get_user_manual',
            data: { UserType: userType, Language: language },
        });
        if (response.status === 200) {
            dispatch({
                type: GET_USER_MANUAL_SUCCESS,
                payload: response.data, 
            });
        } else {
            handleApiError(response, dispatch)
        }
    } catch (error) {
        handleApiError(error, dispatch)
    }
};


export const fetchUserTypes = () => {
    return async (dispatch: any) => {
        try {
            const resp: any = await apis({
                method: "GET",
                url: "/user/getUserTypes",
            })

            if (resp && resp.status === 200) {
                dispatch(setUserTypes(resp.data))
            } else {
                handleApiError(resp, dispatch)
            }
        } catch (e) {
            handleApiError(e, dispatch)
        }
    }


}


export const getpubKeys = (userUid: string) => async (dispatch: Dispatch<any>) => {
    try {
        const response = await apis({
            method: 'GET',
            url: `/chat/get_public_key/${userUid}`,
        });
        if (response.status === 200) {
            const key = response.data.public_key;
            return key

        } else {
            handleApiError(response, dispatch)
            return null
        }
    } catch (error) {
        handleApiError(error, dispatch)
    }
};

export const updateChatLastMessage = (chatID: string | null, lastmessageat: any, currentUserID: string, selectedChatId: any, lastMessage: any) => ({
    type: 'UPDATE_CHAT_LAST_MESSAGE',
    payload: { chatID, lastmessageat, currentUserID, selectedChatId, lastMessage },
});



export const uploadProfilePicture = (file: Blob) => async (dispatch: Dispatch<any>) => {
    try {
        const formData = new FormData();
        formData.append("file", file, "profile.webp");
        const res = await apis({
            "method": "POST",
            "url": "/user-profile/me/profile-picture",
            "data": formData
        })

        if (res && res.status === 200 && res.data?.success) {
            // Invalidate cached profile picture so ProfileImage components re-fetch
            const { invalidateProfilePicCache } = await import('../../components/Header/ProfileImage');
            invalidateProfilePicCache();

            dispatch(showFeedbackModal({
                modalType: "success",
                message: translate("Profile picture updated"),
                duration: 3000,
                showModal: true
            }))
            // Refresh user profile to get updated picture URL
            dispatch(fetchUserProfile());
            return res.data
        } else {
            handleApiError(res, dispatch)
            return null
        }

    } catch(e) {
        handleApiError(e, dispatch)
        return null
    }
}

export const deleteProfilePicture = () => async (dispatch: Dispatch<any>) => {
    try {
        const res = await apis({
            "method": "DELETE",
            "url": "/user-profile/me/profile-picture",
        })

        if (res && res.status === 200 && res.data?.success) {
            const { invalidateProfilePicCache } = await import('../../components/Header/ProfileImage');
            invalidateProfilePicCache();

            dispatch(showFeedbackModal({
                modalType: "success",
                message: translate("Profile picture removed"),
                duration: 3000,
                showModal: true
            }))
            dispatch(fetchUserProfile());
            return res.data
        } else {
            handleApiError(res, dispatch)
            return null
        }
    } catch(e) {
        handleApiError(e, dispatch)
        return null
    }
}
